import React, { useState, useEffect, Fragment, useRef } from 'react';
import { User } from './Directory'
import { Dialog, Disclosure, Popover, Transition } from '@headlessui/react'
import { ChevronDownIcon, XMarkIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/20/solid'
import { RangeSlider } from './RangeSlider';
import { VarianceSelector } from './VarianceSelector';
import { NameFilter } from './NameFilter';
import { displayImperialHeight, convertCmToInches, kgToPounds, poundsToKg, saveFiltersToLocalStorage, fetchFiltersFromLocalStorage, clearFiltersFromLocalStorage, convertInchesToCm } from './directoryUtils';
import { genders, countries, ethnicities } from './directoryData'
import { CheckboxFilter } from './CheckboxFilter';
import { ArrowRightIcon } from 'lucide-react';
import { useIsBreakPoint, useIsMobile } from '../submission_boards/component/useIsMobile';
import { OptionProps } from '../submission_boards/component/SbModel';
import userService from '../services/UserService';
import StateTypeahead from './StateTypeahead';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

interface CurrentUser {
  measure_system: 'imperial' | 'metric';
}

interface UserFilterProps {
  users: User[];
  currentUser: CurrentUser;
  measurementRanges: {
    height: {
      min: number;
      max: number;
    };
    weight: {
      min: number;
      max: number;
    };
  }
  onFilter: (filtered: User[], filterParams: any) => void;
  showFavoritesOnly?: boolean;
  favorites?: Set<string>;
  userGroupsAll?: any;
  firstnameLastNameshow: boolean;
  keywordshow: boolean;
  userGroupshow: boolean;
  masterSkills?: any;
  openFilters?: boolean;
  onClose?:() => void;
  initialFavCollection?: number;
  forceInline?: boolean;
}

const UserFilter: React.FC<UserFilterProps> = ({
  users,
  currentUser,
  onFilter,
  measurementRanges,
  showFavoritesOnly = false,
  userGroupsAll,
  favorites = new Set(),
  firstnameLastNameshow,
  keywordshow,
  userGroupshow,
  masterSkills,
  openFilters,
  onClose,
  initialFavCollection = 0,
  forceInline = false
}) => {
  // const firstnameLastNameshow = true;
  // const keywordshow = false;
  // const userGroupshow = false;
  const isImperial = currentUser.measure_system === 'imperial';
  const { isMobile, isLg1100 } = useIsBreakPoint();

  // measurementRanges={{
  //   height: {
  //     min: 91, // 3ft : 91.44cm
  //     max: 244, // 8ft: 243.84cm
  //   },
  //   weight: {
  //     min: 22, // 50:lbs = 22.6796kg
  //     max: 182, // 400:lbs = 181.437kg
  //   },
  // }}
  const heightBounds: [number, number] = isImperial ? [36, 96] : [91, 244];
  const weightBounds: [number, number] = isImperial ? [48, 401] : [22, 182];
  // const heightBounds: [number, number] = isImperial ? [0, 108] : [0, 275];
  // const weightBounds: [number, number] = isImperial ? [0, 1000] : [0, 500];

  const [keyword, setKeyword] = useState<string>(() => fetchFiltersFromLocalStorage('keyword', ''));
  const [name, setName] = useState<{ firstName: string; lastName: string }>({ firstName: '', lastName: '' });
  const [selectedCountries, setSelectedCountries] = useState<string[]>(() => fetchFiltersFromLocalStorage('selectedCountries', []));
  const [selectedState, setSelectedState] = useState<string>(() => fetchFiltersFromLocalStorage('selectedState', ''));
  const [stateOptions, setStateOptions] = useState<OptionProps[]>([]);
  const [selectedEthnicities, setSelectedEthnicities] = useState<string[]>(() => fetchFiltersFromLocalStorage('selectedEthnicities', []));
  const [selectedRoles, setSelectedRoles] = useState<string[]>(() => fetchFiltersFromLocalStorage('selectedRoles', []));
  const [heightRange, setHeightRange] = useState<[number, number]>(() => fetchFiltersFromLocalStorage('heightRange', heightBounds));
  const [weightRange, setWeightRange] = useState<[number, number]>(() => fetchFiltersFromLocalStorage('weightRange', weightBounds));
  const [selectedGenders, setSelectedGenders] = useState<string[]>(() => fetchFiltersFromLocalStorage('selectedGenders', []));
  const [selectedUserGroups, setSelectedUserGroups] = useState<number[]>(() => fetchFiltersFromLocalStorage('selectedUserGroups', []));
  const [favCollection, setFavCollection] = useState<number>(() => fetchFiltersFromLocalStorage('favCollection', initialFavCollection));
  const [userGroups, setUserGroups] = useState([]);

  const [selectedSpecialtySkills, setSelectedSpecialtySkills] = useState(() => fetchFiltersFromLocalStorage('selectedSpecialtySkills', []));
  const [specialtySkills, setspecialtySkills] = useState([]);
  const [openShowMobileFilter, setOpenShowMobileFilter] = useState(false)
  const [isFiltered, setIsFiltered] = useState(false);

  const availableRoles = React.useMemo(() => {
    const roleSet = new Set<string>();
    users.forEach(user => {
      if (user.role) {
        roleSet.add(user.role);
      }
    });
    return Array.from(roleSet).sort();
  }, [users]);

  useEffect(() => {
    setOpenShowMobileFilter(openFilters || false)
  }, [openFilters]);

  useEffect(() => {
    if (userGroupsAll) {
      // console.log('userGroupsAll', userGroupsAll)
      setUserGroups(userGroupsAll);
    }
  }, [userGroupsAll])

  useEffect(() => {
    if (masterSkills) {
      // console.log('masterSkills', masterSkills)
      setspecialtySkills(masterSkills);
    }
  }, [masterSkills])


  const isAnyFilterActive = React.useMemo(() => {
    return (
      keyword !== '' ||
      name.firstName !== '' ||
      name.lastName !== '' ||
      selectedCountries.length > 0 ||
      selectedState !== '' ||
      selectedUserGroups.length > 0 ||
      selectedEthnicities.length > 0 ||
      selectedRoles.length > 0 ||
      selectedGenders.length > 0 ||
      heightRange[0] !== heightBounds[0] ||
      heightRange[1] !== heightBounds[1] ||
      weightRange[0] !== weightBounds[0] ||
      weightRange[1] !== weightBounds[1] ||
      selectedSpecialtySkills.length > 0 ||
      favCollection > 0

    );
  }, [
    keyword,
    name.firstName,
    name.lastName,
    selectedCountries,
    selectedState,
    selectedEthnicities,
    selectedRoles,
    selectedGenders,
    heightRange,
    heightBounds,
    weightRange,
    weightBounds,
    selectedUserGroups,
    favCollection,
    selectedSpecialtySkills,
  ]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName({ ...name, [e.target.name]: e.target.value });
    saveFiltersToLocalStorage('name', { ...name, [e.target.name]: e.target.value });
  }
  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    saveFiltersToLocalStorage('keyword', e.target.value);
  }

  const handleCountryChange = (country: string) => {
    setSelectedCountries((prev) => {
      const updatedCountries = prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country];
      saveFiltersToLocalStorage('selectedCountries', updatedCountries);
      return updatedCountries;
    }
    );
  };

  const handleCountryClear = () => {
    setSelectedCountries([]);
    clearFiltersFromLocalStorage('selectedCountries');
    handleStateClear();
  }

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    saveFiltersToLocalStorage('selectedState', state);
  };

  const handleStateClear = () => {
    setSelectedState('');
    clearFiltersFromLocalStorage('selectedState');
  }

  useEffect(() => {
    if (selectedCountries.length === 1) {
      userService.getStates(selectedCountries[0])
        .then((states: OptionProps[]) => {
          setStateOptions(states);
          if (selectedState && !states.some((s) => s.id === selectedState)) {
            handleStateClear();
          }
        })
        .catch((error) => {
          console.error('Error fetching states:', error);
          setStateOptions([]);
        });
    } else {
      setStateOptions([]);
      if (selectedState) {
        handleStateClear();
      }
    }
  }, [selectedCountries]);

  const handleUserGroupsChange = (id: number) => {
    setSelectedUserGroups((prev) => {
      const updatedGroups = prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id];
      saveFiltersToLocalStorage('selectedUserGroups', updatedGroups);
      return updatedGroups;
    }
    );
  };

  const handleUserGroupsClear = () => {
    handleFavCollectionChange(initialFavCollection);
    setSelectedUserGroups([]);
    clearFiltersFromLocalStorage('selectedUserGroups');
  }

  const handleFavCollectionChange = (val: number) => {
    setFavCollection(val);
    saveFiltersToLocalStorage('favCollection', val);
  }
  const handleSpecialtySkillsChange = (id: number) => {
    setSelectedSpecialtySkills((prev) => {
      const _skills = prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id];
      saveFiltersToLocalStorage('selectedSpecialtySkills', _skills);
      return _skills;
    }
    );
  };
  const handleRemoveSpecialtySkillsChange = (id: number) => {
    setSelectedSpecialtySkills((prev) => {
      const _skills = prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id];
      saveFiltersToLocalStorage('selectedSpecialtySkills', _skills);
      return _skills;
    }
    );
  };

  const handleSpecialtySkillsClear = () => {
    setSelectedSpecialtySkills([]);
    clearFiltersFromLocalStorage('selectedSpecialtySkills');
  }

  const handleRoleChange = (role: string) => {
    setSelectedRoles((prev) => {
      const updatedRoles = prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role];
      saveFiltersToLocalStorage('selectedRoles', updatedRoles);
      return updatedRoles;
    });
  };

  const handleRoleClear = () => {
    setSelectedRoles([]);
    clearFiltersFromLocalStorage('selectedRoles');
  }

  const handleEthnicityChange = (ethnicity: string) => {
    setSelectedEthnicities((prev) => {
      const updatedEthnicities = prev.includes(ethnicity) ? prev.filter(c => c !== ethnicity) : [...prev, ethnicity]
      saveFiltersToLocalStorage('selectedEthnicities', updatedEthnicities);
      return updatedEthnicities;
    }
    );
  };

  const handleEthnicityClear = () => {
    setSelectedEthnicities([]);
    clearFiltersFromLocalStorage('selectedEthnicities');
  }

  const handleGenderChange = (gender: string) => {
    setSelectedGenders((prev) => {
      const updatedGenders = prev.includes(gender) ? prev.filter(g => g !== gender) : [...prev, gender]
      saveFiltersToLocalStorage('selectedGenders', updatedGenders);
      return updatedGenders;
    }
    );
  };

  const handleGenderClear = () => {
    setSelectedGenders([]);
    clearFiltersFromLocalStorage('selectedGenders');
  }

  const handleHeightChange = (values: [number, number]) => {
    const [newMinHeight, newMaxHeight] = values;

    if (newMaxHeight < newMinHeight) {
      setHeightRange([newMinHeight, newMinHeight + 1]);
      saveFiltersToLocalStorage('heightRange', [newMinHeight, newMinHeight + 1]);
    } else {
      setHeightRange([newMinHeight, newMaxHeight]);
      saveFiltersToLocalStorage('heightRange', [newMinHeight, newMaxHeight]);
    }
  };

  const handleHeightClear = () => {
    setHeightRange(heightBounds);
    clearFiltersFromLocalStorage('heightRange');
  }

  const handleWeightChange = (values: [number, number]) => {
    const [newMinWeight, newMaxWeight] = values;

    if (newMaxWeight < newMinWeight) {
      setWeightRange([newMinWeight, newMinWeight + 1]);
      saveFiltersToLocalStorage('weightRange', [newMinWeight, newMinWeight + 1]);
    } else {
      setWeightRange([newMinWeight, newMaxWeight]);
      saveFiltersToLocalStorage('weightRange', [newMinWeight, newMaxWeight]);
    }
  };

  const handleWeightClear = () => {
    setWeightRange(weightBounds);
    clearFiltersFromLocalStorage('weightRange');
  }

  const filters = [{
    id: 'gender',
    name: 'Gender',
    options: genders,
    value: selectedGenders,
    handler: handleGenderChange,
    clear: handleGenderClear,
    type: 'checkbox',
    label: null,
    dirty: selectedGenders.length > 0,
  },
  {
    id: 'ethnicity',
    name: 'Ethnicity',
    options: ethnicities,
    value: selectedEthnicities,
    handler: handleEthnicityChange,
    clear: handleEthnicityClear,
    type: 'checkbox',
    label: null,
    dirty: selectedEthnicities.length > 0,
  },{
    id: 'country',
    name: 'Country',
    options: countries,
    value: selectedCountries,
    handler: handleCountryChange,
    clear: handleCountryClear,
    type: 'checkbox',
    label: null,
    dirty: selectedCountries.length > 0,
  },
  ...(selectedCountries.length === 1 && stateOptions.length > 0 ? [{
    id: 'state',
    name: 'Location (State)',
    options: stateOptions,
    value: selectedState,
    handler: handleStateChange,
    clear: handleStateClear,
    type: 'combobox',
    label: selectedState ? (stateOptions.find((s) => s.id === selectedState)?.name || '') : null,
    dirty: selectedState !== '',
  }] : []),

  {
    id: 'weight',
    name: 'Weight',
    options: [weightBounds],
    handler: handleWeightChange,
    clear: handleWeightClear,
    value: weightRange,
    type: 'range',
    label: isImperial ? `${weightRange[0]} - ${weightRange[1]} lbs` : `${weightRange[0]} - ${weightRange[1]} kg`,
    minLabel: isImperial ? `${weightRange[0]} lbs` : `${weightRange[0]} kg`,
    maxLabel: isImperial ? `${weightRange[1]} lbs` : `${weightRange[1]} kg`,
    formatLabel: (value) => isImperial ? `${value} lbs` : `${value} kg`,
    dirty: weightRange[0] !== weightBounds[0] || weightRange[1] !== weightBounds[1],
    step: 1,
  },
  {
    id: 'height',
    name: 'Height',
    options: [heightBounds],
    handler: handleHeightChange,
    clear: handleHeightClear,
    value: heightRange,
    type: 'range',
    label: isImperial ? `${displayImperialHeight(heightRange[0])} - ${displayImperialHeight(heightRange[1])}` : `${heightRange[0]} - ${heightRange[1]} cm`,
    minLabel: isImperial ? `${displayImperialHeight(heightRange[0])}` : `${heightRange[0]} cm`,
    maxLabel: isImperial ? `${displayImperialHeight(heightRange[1])}` : `${heightRange[1]} cm`,
    formatLabel: (value) => isImperial ? `${displayImperialHeight(value)}` : `${value} cm`,
    dirty: heightRange[0] !== heightBounds[0] || heightRange[1] !== heightBounds[1],
    step: 1,
  }, 
  ...(userGroupshow ? [{
    id: 'usergroups',
    name: 'Filter by favorites',
    options: userGroups,
    value: selectedUserGroups,
    handler: handleUserGroupsChange,
    clear: handleUserGroupsClear,
    type: 'checkbox',
    label: null,
    dirty: selectedUserGroups.length > 0,
  }] : []),
  ...( specialtySkills.length > 0 ? [{
    id: 'specialtyskills_id',
    name: 'Filter by Skills',
    options: specialtySkills,
    value: selectedSpecialtySkills,
    handler: handleSpecialtySkillsChange,
    clear: handleSpecialtySkillsClear,
    type: 'checkbox',
    label: null,
    dirty: selectedSpecialtySkills.length > 0,
  }] : []),

  ...(availableRoles.length > 0 ? [{
    id: 'role',
    name: 'Role',
    options: availableRoles.map(role => ({ id: role, name: role })),
    value: selectedRoles,
    handler: handleRoleChange,
    clear: handleRoleClear,
    type: 'checkbox',
    label: null,
    dirty: selectedRoles.length > 0,
  }] : [])
  ]

  // useEffect(() => {
  //   applyFilters();
  // }, [selectedCountries, selectedEthnicities, selectedRoles, heightRange, weightRange, selectedGenders, name, showFavoritesOnly, favorites]);
  useEffect(() => {
    console.log('gender changed')
    applyFilters();
  }, [selectedCountries, selectedState, selectedEthnicities, selectedGenders, favCollection, selectedUserGroups, 
    name, selectedSpecialtySkills, keyword, heightRange, weightRange]);

  useEffect(() => {
    console.log('heightRange, weightRange changed')
    // applyFilters();
  }, [heightRange, weightRange]);
  useEffect(() => {
    console.log('keyword changed')
    // applyFilters();
  }, [keyword]);

  const rangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleFilterChange = (filter, option) => {
    if (filter.type === 'checkbox') {
      filter.handler(option.id);
    } else if (filter.type === 'combobox') {
      filter.handler(option.id);
    } else if (filter.type === 'range') {
      if (rangeTimeoutRef.current) {
        clearTimeout(rangeTimeoutRef.current);
      }
      rangeTimeoutRef.current = setTimeout(() => {
        filter.handler(option);
      }, 500); // Reduced to 500ms for better responsiveness
    }
  }

  const handleEnterPress = () => {
    applyFilters();
  };

  const applyFilters = () => {
    let filterPresent = false;
    let filterParams: any = {};
    let filtered = users;

    if (favCollection == 1) {
      filterPresent = true;
      filterParams.onlyfav = true;
      // filtered = filtered.filter(user => favorites.has(user.uuid));
    } else if (favCollection == 2) {
      filterPresent = true;
      if (selectedUserGroups.length > 0) {
        filterParams.selectedUserGroups = selectedUserGroups;
        delete filterParams.onlyfav;
      } else {
        filterParams.onlyfav = true;
      }
    }
    if (keyword.trim().length > 0) {
      filterParams.keyword = keyword.trim();
      filterPresent = true;
    }
    if (name.firstName || name.lastName) {
      if (name.firstName) {
        filterParams.firstName = name.firstName;
        filterPresent = true;
      }
      if (name.lastName) {
        filterParams.lastName = name.lastName;
        filterPresent = true;
      }
      filtered = filtered.filter(user => {
        if (name.firstName && name.lastName) {
          return user.firstName?.toLowerCase().includes(name.firstName?.toLowerCase()) &&
            user.lastName?.toLowerCase().includes(name.lastName?.toLowerCase());
        } else if (name.firstName) {
          return user.firstName?.toLowerCase().includes(name.firstName?.toLowerCase());
        } else if (name.lastName) {
          return user.lastName?.toLowerCase().includes(name.lastName?.toLowerCase());
        }
      });
    }

    if (selectedCountries.length) {
      filterParams.selectedCountries = selectedCountries;
      filterPresent = true;
      filtered = filtered.filter(user => selectedCountries.includes(user.country));
    } else {
      filterParams.selectedCountries = undefined;
    }

    if (selectedState) {
      filterParams.selectedState = selectedState;
      filterPresent = true;
    } else {
      filterParams.selectedState = undefined;
    }

    // if (selectedRoles.length) {
    //   filterParams.selectedRoles = selectedRoles;
    //   filtered = filtered.filter(user => user.role && selectedRoles.includes(user.role));
    // } else {
    //     filterParams.selectedRoles = undefined;

    // }

    if (selectedEthnicities.length) {
      filterPresent = true;
      filterParams.selectedEthnicities = selectedEthnicities;
      filtered = filtered.filter(user =>
        user.ethnicities?.some(ethnicity => selectedEthnicities.includes(ethnicity))
      );
    } else {
      filterParams.selectedEthnicities = undefined;
    }

    if (heightRange) {
      if (heightRange && (heightRange[0] !== heightBounds[0] || heightRange[1] !== heightBounds[1])) {
        // if (heightRange !== heightBounds) {
        console.log('heightRange', heightRange, 'heightBounds', heightBounds);
        filterPresent = true;
        if (isImperial) {
          filterParams.heightRange = [convertInchesToCm(heightRange[0]), convertInchesToCm(heightRange[1])];
        } else {
          filterParams.heightRange = heightRange;
        }
        filtered = filtered.filter(user => {
          if (isImperial) {
            const heightInInches = convertCmToInches(user.height);
            return heightInInches >= heightRange[0] && heightInInches <= heightRange[1];
          } else {
            return user.height >= heightRange[0] && user.height <= heightRange[1];
          }
        });
      } else {
        filterParams.heightRange = undefined;
      }
    }

    if (weightRange) {
      if (weightRange && (weightRange[0] !== weightBounds[0] || weightRange[1] !== weightBounds[1])) {
        filterPresent = true;
        // if (weightBounds !== weightRange) {
        if (isImperial) {
          const minKg = poundsToKg(weightRange[0]);
          const maxKg = poundsToKg(weightRange[1]);
          filterParams.weightRange = [minKg, maxKg];
          filtered = filtered.filter(user => user.weight >= minKg && user.weight <= maxKg);
        } else {
          filterParams.weightRange = weightRange;
          filtered = filtered.filter(user => user.weight >= weightRange[0] && user.weight <= weightRange[1]);
        }
      } else {
        filterParams.weightRange = undefined;
      }
    }

    if (selectedGenders.length) {
      filterPresent = true;
      filterParams.selectedGenders = selectedGenders;
      filtered = filtered.filter(user => selectedGenders.includes(user.gender));
    } else {
      filterParams.selectedGenders = undefined;
    }
    if (selectedSpecialtySkills.length) {
      filterPresent = true;
      filterParams.selectedSpecialtySkills = selectedSpecialtySkills;
      // filtered = filtered.filter(user =>
      //   user.ethnicities?.some(ethnicity => selectedEthnicities.includes(ethnicity))
      // );
    } else {
      filterParams.selectedSpecialtySkills = undefined;
    }

    setIsFiltered(filterPresent)
    if (filterPresent) {
      filterParams.filterPresent = 1;
      onFilter(filtered, filterParams);
    } else {
      // No filters active - notify parent to show all data
      onFilter(users, { filterPresent: 0 });
    }
  };

  const clearFilters = () => {
    handleSpecialtySkillsClear();
    handleUserGroupsClear();
    handleCountryClear();
    handleStateClear();
    handleRoleClear();
    handleEthnicityClear();
    handleGenderClear();
    handleHeightClear();
    handleWeightClear();
    setName({ firstName: '', lastName: '' });
    clearFiltersFromLocalStorage('name');
    setKeyword('')
    clearFiltersFromLocalStorage('keyword');
    setIsFiltered(false);
    onFilter([], {
      filterPresent: 2
    });
  }
  const renderSkills = () => {
    return <>
      <div
        className="z-10 bg-white rounded-lg max-h-60 overflow-y-auto primary-color"
        style={{
          // border: '1px solid #374151',
          minWidth: '100%',
        }}
      >
        <div className="flex mb-2 w-full justify-start px-2">
          <div onClick={() => {
            handleSpecialtySkillsClear();
          }} className="flex underline cursor-pointer py-1.5 text-xs font-semibold tabular-nums text-gray-700">
            Clear selection
          </div>
        </div>

        {specialtySkills.map((skill, index) => {
          let showName = false;
          if (index > 0) {
            if (skill.name !== specialtySkills[index - 1].name) {
              showName = true;
            }
          } else {
            showName = true;
          }
          return (
            <span
                  key={index}
            >
              {showName && (
                <div
                  key={index}
                  className="flex flex-row items-center px-2 py-1 gap-2-1 "
                >
                  <div className="flex flex-col ">
                    <span className="text-sm font-medium primary-color">
                      {skill.name}:
                    </span>
                  </div>
                </div>
              )}
              <div
                key={skill.id}
                onClick={async (e) => {
                  // e.preventDefault();
                  // e.stopPropagation();
                  // handleSpecialtySkillsChange(skill.id);
                  //await handleSelectSkill(skill)
                }}
                className="flex items-center justify-start px-4 py-1 hover:bg-blue-50 cursor-pointer gap-1-1"
              >
                <div className="w-4 h-4 flex items-center">
                  <input
                    id={`filter-${skill.id}`}
                    // name={`${filter.id}[]`}
                    defaultValue={skill.id}
                    checked={selectedSpecialtySkills.includes(skill.id)}
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.target.checked) {
                        handleSpecialtySkillsChange(skill.id);
                      } else {
                        handleRemoveSpecialtySkillsChange(skill.id);
                      }
                    }}

                  />
                </div>
                <div className="text-sm font-medium text-gray-800">
                  {skill.sub_skill}
                </div>
              </div>
            </span>
          );
        })}
      </div>
    </>;
  }

  return (
    <div className={"filter-root w-full" + ((isMobile && !forceInline)?(openShowMobileFilter?'  ': ' hidden '):'')}>

      {/* mobile filters */}
      {!forceInline && (
      <Transition.Root show={openShowMobileFilter} as={Fragment} >
        <Dialog as="div" className="relative z-40 block sm:hidden " onClose={()=>
        {
          // setOpen(false);
          onClose && onClose();

        }}
          style={{
            zIndex: '60'
          }}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative ml-auto flex flex-col h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl pt-20">
                <div className="flex items-center justify-between px-4">
                  <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                    onClick={() => {
                        // setOpen(false);
                        onClose && onClose();
                    }}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                {isAnyFilterActive && (
                  <div className="flex mb-2 w-full justify-end px-2">
                    <div onClick={clearFilters} className="flex underline cursor-pointer py-1.5 text-xs font-semibold tabular-nums text-gray-700">
                      Clear Filters
                    </div>
                  </div>
                )}
                <div className="mt-4">
                  {keywordshow && <Disclosure as="div" key={`filter-mobile-keyword`} className={classNames(keyword?.length > 0 ? "bg-blue-50 text-blue-400" : "bg-white text-gray-400", "border-t border-gray-200 px-4 py-6")}>
                    {({ open }) => (
                      <>
                        <h3 className="-mx-2 -my-3 flow-root">
                          <Disclosure.Button className={classNames(keyword?.length > 0 ? "bg-blue-50 text-blue-400" : "bg-white text-gray-400", "flex w-full items-center justify-between  px-2 py-3 text-sm ")}>
                            <span className="flex font-medium text-gray-900">Keyword
                              {keyword?.length > 0 &&
                                <span className="ml-1.5 rounded bg-blue-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                                  1
                                </span>
                              }
                            </span>

                            <span className="ml-6 flex items-center">
                              <ChevronDownIcon
                                className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                aria-hidden="true"
                              />
                            </span>
                          </Disclosure.Button>
                        </h3>

                        <Disclosure.Panel className="pt-6">
                          <div className="space-y-6">
                            <input
                              type="name"
                              name="keyword"
                              id="keyword"
                              className="w-full rounded-md border px-3.5 py-2.5 text-gray-900 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-insetsm:text-sm sm:leading-6"
                              placeholder="keyword, Name, Last Name"
                              onChange={handleKeywordChange}
                              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Enter' && handleEnterPress) {
                                  handleEnterPress();
                                }
                              }}
                              value={keyword}
                            />
                          </div>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>}
                  {firstnameLastNameshow && <Disclosure as="div" key={`filter-mobile-name`} className={classNames(name.firstName?.length > 0 || name.lastName?.length ? "bg-blue-50 text-blue-400" : "bg-white text-gray-400", "border-t border-gray-200 px-4 py-6")}>
                    {({ open }) => (
                      <>
                        <h3 className="-mx-2 -my-3 flow-root">
                          <Disclosure.Button className={classNames(name.firstName?.length > 0 || name.lastName?.length ? "bg-blue-50 text-blue-400" : "bg-white text-gray-400", "flex w-full items-center justify-between  px-2 py-3 text-sm ")}>
                            <span className="flex font-medium text-gray-900">Name
                              {name.firstName?.length > 0 || name.lastName?.length > 0 &&
                                <span className="ml-1.5 rounded bg-blue-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                                  1
                                </span>
                              }
                            </span>

                            <span className="ml-6 flex items-center">
                              <ChevronDownIcon
                                className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                aria-hidden="true"
                              />
                            </span>
                          </Disclosure.Button>
                        </h3>
                        <Disclosure.Panel className="pt-6">
                          <div className="space-y-6">
                            <NameFilter name={name} onChange={handleNameChange} onEnterPress={handleEnterPress} />
                          </div>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                  }
                  {filters.map((filter) => (
                    <Disclosure as="div" key={`filter-mobile-${filter.id}`} className={classNames(filter.dirty ? "bg-blue-50 text-blue-800" : "bg-white text-gray-900", "border-t border-gray-200 px-4 py-6")}>
                      {({ open }) => (
                        <>
                          <h3 className="-mx-2 -my-3 flow-root">
                            <Disclosure.Button className="flex w-full items-center justify-between px-2 py-3 text-sm">
                              <span className="flex font-medium flex-col justify-start items-start">
                                {/* {filter.dirty &&
                                  <span onClick={filter.clear} className="mt-0.5 mr-1.5 rounded bg-blue-200 px-0.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                                    <XMarkIcon className="h-3 w-3" />
                                  </span>
                                } */}
                                {filter.name}
                                {filter.type === 'checkbox' && filter.value.length > 0 &&
                                  <span className="text-sm text-gray-700">
                                    {
                                      filter.id == 'usergroups' && favCollection == 1 ? 'All My Favorites'
                                        : (
                                          filter.id == 'usergroups' && favCollection == 2 && selectedUserGroups.length > 0 ?
                                            userGroups.filter((item) => selectedUserGroups.indexOf(item.id) >= 0).map((item) => item.name).join(', ') :
                                            (
                                              filter.id == 'specialtyskills_id' && selectedSpecialtySkills.length > 0 ?
                                              specialtySkills.filter((item) => selectedSpecialtySkills.indexOf(item.id) >= 0).map((item) => item.sub_skill).join(', ')
                                              :
                                              filter.value.join(', ')
                                            )
                                        )
                                    }
                                  </span>
                                }
                                {/* {filter.type === 'checkbox' && filter.value.length > 0 &&
                                  <span className="ml-1.5 rounded bg-blue-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                                    {filter.value.join(', ')}
                                  </span>
                                } */}
                                {filter.type === 'range' && filter.dirty &&
                                  <span className="ml-1.5 rounded bg-blue-200 px-1 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                                    {filter.label}
                                  </span>
                                }
                                {filter.type === 'combobox' && filter.dirty &&
                                  <span className="text-sm text-gray-700">
                                    {filter.label}
                                  </span>
                                }
                              </span>
                              <span className="ml-6 flex items-center">
                                <ChevronDownIcon
                                  className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </h3>
                          <Disclosure.Panel className="pt-6 ">
                            <div className="space-y-6">
                              {filter.type === 'combobox' && (
                                <StateTypeahead
                                  id={`combobox-mobile-${filter.id}`}
                                  options={filter.options as OptionProps[]}
                                  initialValue={(filter.options as OptionProps[]).find((o) => o.id === filter.value) || { id: '', name: '' }}
                                  onChange={(option) => (filter.handler as (id: any) => void)(option.id)}
                                  placeholder={`Type to search ${filter.name}`}
                                />
                              )}
                              {filter.type === 'checkbox' && <>{
                                (filter.id == 'usergroups' || filter.id == 'specialtyskills_id') ? <>
                                {filter.id == 'usergroups' && <div className='flex w-full flex-col gap-2-1'>
                                  <div className='flex w-full flex-row gap-2-1'>
                                    <input
                                      id={`my_fav`}
                                      type="checkbox"
                                      // checked={selectedUserGroups.length<=0?false: (selectedUserGroups.filter((item) => item <= 0).length > 0?true:false)}
                                      checked={favCollection == 1}
                                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedUserGroups([]);
                                          clearFiltersFromLocalStorage('selectedUserGroups');
                                          handleFavCollectionChange(1);
                                        } else {
                                          handleFavCollectionChange(0);
                                        }
                                      }}
                                    />
                                    <div className='whitespace-nowrap pr-6 text-sm font-medium text-gray-900'>All My Favorites</div>
                                  </div>
                                  <div className='flex w-full flex-col gap-2-1'>
                                    <div className='flex w-full flex-row gap-2-1'>
                                      <input
                                        id={`my_collections`}
                                        type="checkbox"
                                        checked={favCollection == 2}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            handleFavCollectionChange(2);
                                          } else {
                                            handleFavCollectionChange(0);
                                            setSelectedUserGroups([]);
                                            clearFiltersFromLocalStorage('selectedUserGroups');
                                          }
                                        }}
                                      />
                                      <div className='whitespace-nowrap pr-6 text-sm font-medium text-gray-900'>Collections</div>

                                    </div>
                                    <div className='flex w-full flex-col pl-4 gap-2-1 max-h-60 overflow-y-auto scrollable-content'>
                                      {favCollection == 2 &&
                                        <CheckboxFilter filter={filter} onChange={handleFilterChange} />}
                                    </div>
                                  </div>
                                </div>}
                                {filter.id == 'specialtyskills_id' && <>
                                  {renderSkills()}
                                </>}
                              </>
                                : (<CheckboxFilter filter={filter} onChange={handleFilterChange} />)
                              }</>
                            }
                              {filter.type === 'range' &&
                                <div className="mb-3">
                                  {(filter.id === 'height' || filter.id === 'weight') ? (
                                    <VarianceSelector 
                                      filter={filter} 
                                      onChange={handleFilterChange} 
                                      isImperial={isImperial}
                                      formatLabel={filter.formatLabel}
                                    />
                                  ) : (
                                    <RangeSlider filter={filter} onChange={handleFilterChange} />
                                  )}
                                </div>
                              }
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      )}
      {/* end mobile filters */}

      {/* desktop filters */}

      <section className={'flex flex-col ' + ((isMobile && !forceInline)?' hidden ':'')} aria-labelledby="filter-heading">
        <h2 id="filter-heading" className="sr-only">
          Filters
        </h2>

        <div className="border-gray-200 pb-4">
          <div className="mx-auto flex max-w-7xl items-left justify-between">
            {/* <button
              type="button"
              className="flex sm:hidden bg-white border rounded-md py-2.5 px-3.5 gap-3 justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
              onClick={() => setOpen(true)}
            >
              <AdjustmentsHorizontalIcon className="w-6 h-6" /> <span className="pt-0.5">All Filters</span>
            </button> */}

            <div className="flex flex-col gap-2">
              {firstnameLastNameshow && <NameFilter name={name} onChange={handleNameChange} onEnterPress={handleEnterPress} />}
              {keywordshow && <div className="space-y-6">
                <input
                  type="name"
                  name="keyword"
                  id="keyword"
                  className="w-full rounded-md border px-3.5 py-2.5 text-gray-900 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-insetsm:text-sm sm:leading-6"
                  placeholder="Name, Last Name"
                  onChange={handleKeywordChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && handleEnterPress) {
                      handleEnterPress();
                    }
                  }}
                  value={keyword}
                />
              </div>}
              <div className='flex flex-row justify-between'>
                <div className="flex py-1.5 text-xs font-semibold tabular-nums text-gray-700">Filter by:</div>
                {isAnyFilterActive && (
                  <div className="flex mb-2">
                    <div onClick={clearFilters} className="flex underline cursor-pointer py-1.5 text-xs font-semibold tabular-nums text-gray-700">
                      Clear Filters
                    </div>
                  </div>
                )}
              </div>
              <div className="flow-root">
                <Popover.Group className="flex flex-col gap-3 items-start justify-start">
                  {/* <button
                    type="button"
                    className="group bg-white border rounded-md py-2 px-3 inline-flex gap-3 justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                    onClick={() => setOpen(true)}
                  >
                    <AdjustmentsHorizontalIcon className="w-6 h-6" />
                  </button> */}
                  {filters.map((filter) => (
                    <Popover key={filter.id} className="relative inline-block text-left w-full">
                      <Popover.Button className={classNames(filter.dirty ? "bg-blue-50 text-blue-800"
                        : "text-gray-700 bg-white hover:text-gray-900",
                        "w-full group  border rounded-md py-2 px-2-1 inline-flex justify-between text-sm font-medium gap-2-1")}>
                        {/* {filter.dirty &&
                          <span onClick={filter.clear} className="mt-0.5 mr-1.5 rounded bg-blue-200 px-0.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                            <XMarkIcon className="h-3 w-3" />
                          </span>
                        } */}
                        <div className='flex flex-col text-left'>
                          <span className="text-xs text-gray-500">{filter.name}</span>
                          {filter.type === 'checkbox' && filter.value.length > 0 &&
                            <span className="text-sm text-gray-700">
                              {/* {filter.value.length} */}
                              {
                                filter.id == 'usergroups' && favCollection == 1 ? 'All My Favorites'
                                  : (
                                    filter.id == 'usergroups' && favCollection == 2 && selectedUserGroups.length > 0 ?
                                      userGroups.filter((item) => selectedUserGroups.indexOf(item.id) >= 0).map((item) => item.name).join(', ') :
                                      (
                                        filter.id == 'specialtyskills_id' && selectedSpecialtySkills.length > 0 ?
                                          specialtySkills.filter((item) => selectedSpecialtySkills.indexOf(item.id) >= 0).map((item) => item.sub_skill).join(', ')
                                          :
                                          filter.value.join(', ')
                                      )
                                  )
                              }
                            </span>
                          }
                          {filter.type === 'range' && filter.value.length > 0 &&
                            <span className="text-sm text-gray-700">
                              {/* {filter.value.length} */}
                              {filter.label}
                            </span>
                          }
                          {filter.type === 'combobox' && filter.dirty &&
                            <span className="text-sm text-gray-700">
                              {filter.label}
                            </span>
                          }
                        </div>
                        <ChevronDownIcon
                          className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Popover.Panel className={classNames(
                          "absolute left-0 z-50 mt-0 origin-top-right rounded-md bg-white p-3 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none",
                          (filter.id === 'weight' || filter.id === 'height') ? "w-72 origin-bottom-left bottom-full" : "w-full",
                          (filter.id === 'usergroups' || filter.id === 'specialtyskills_id') ? "origin-bottom-right bottom-full" : ""
                        )}>
                          <form className="space-y-4">

                            {filter.type === 'combobox' && (
                              <StateTypeahead
                                id={`combobox-${filter.id}`}
                                options={filter.options as OptionProps[]}
                                initialValue={(filter.options as OptionProps[]).find((o) => o.id === filter.value) || { id: '', name: '' }}
                                onChange={(option) => (filter.handler as (id: any) => void)(option.id)}
                                placeholder={`Type to search ${filter.name}`}
                              />
                            )}
                            {filter.type === 'checkbox' && <>{
                              (filter.id == 'usergroups' || filter.id == 'specialtyskills_id') ? <>
                                {filter.id == 'usergroups' && <div className='flex w-full flex-col gap-2-1'>
                                  <div className='flex w-full flex-row gap-2-1'>
                                    <input
                                      id={`my_fav`}
                                      type="checkbox"
                                      // checked={selectedUserGroups.length<=0?false: (selectedUserGroups.filter((item) => item <= 0).length > 0?true:false)}
                                      checked={favCollection == 1}
                                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedUserGroups([]);
                                          clearFiltersFromLocalStorage('selectedUserGroups');
                                          handleFavCollectionChange(1);
                                        } else {
                                          handleFavCollectionChange(0);
                                        }
                                      }}
                                    />
                                    <div className='whitespace-nowrap pr-6 text-sm font-medium text-gray-900'>All My Favorites</div>
                                  </div>
                                  <div className='flex w-full flex-col gap-2-1'>
                                    <div className='flex w-full flex-row gap-2-1'>
                                      <input
                                        id={`my_collections`}
                                        type="checkbox"
                                        checked={favCollection == 2}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            handleFavCollectionChange(2);
                                          } else {
                                            handleFavCollectionChange(0);
                                            setSelectedUserGroups([]);
                                            clearFiltersFromLocalStorage('selectedUserGroups');
                                          }
                                        }}
                                      />
                                      <div className='whitespace-nowrap pr-6 text-sm font-medium text-gray-900'>Collections</div>

                                    </div>
                                    <div className='flex w-full flex-col pl-4 gap-2-1 max-h-60 overflow-y-auto scrollable-content'>
                                      {favCollection == 2 &&
                                        <CheckboxFilter filter={filter} onChange={handleFilterChange} />}
                                    </div>
                                  </div>
                                </div> }
                                {filter.id == 'specialtyskills_id' && <>
                                  {renderSkills()}
                                </>}
                              </>
                                : (<CheckboxFilter filter={filter} onChange={handleFilterChange} />)
                                }</>
                            }
                            {filter.type === 'range' &&
                              <div className="p-1">
                                {(filter.id === 'height' || filter.id === 'weight') ? (
                                  <VarianceSelector 
                                    filter={filter} 
                                    onChange={handleFilterChange} 
                                    isImperial={isImperial}
                                    formatLabel={filter.formatLabel}
                                  />
                                ) : (
                                  <RangeSlider filter={filter} onChange={handleFilterChange} />
                                )}
                              </div>
                            }
                          </form>
                        </Popover.Panel>
                      </Transition>
                    </Popover>
                  ))}
                </Popover.Group>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div >
  );
}

export default UserFilter;
